import React, { InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { BetError } from 'shared/constants/shared-enums';
import { CurrencyType } from 'shared/web3';

interface IBetInputProps extends InputHTMLAttributes<HTMLInputElement> {
  value: string | number;
  valueHandler: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  className?: string;
  balance?: number;
  disabled?: boolean;
  betError?: BetError;
  selectedCurrency: CurrencyType;
}

const BetInput = ({
  value,
  valueHandler,
  type = 'text',
  className,
  balance,
  disabled,
  betError,
  selectedCurrency,
  ...props
}: IBetInputProps) => {
  const { t } = useTranslation();

  const errorMessages: Record<string, string> = {
    [BetError.LOW_BET]: t('modals.setBet.minimumBetError', {
      minBet: selectedCurrency?.minBet,
      symbol: selectedCurrency?.symbol,
    }),
    [BetError.INVALID_BET]: t('modals.setBet.invalidBet'),
    [BetError.LOW_BALANCE]: t('modals.setBet.insufficientBalance'),
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        data-testid="bet-container"
        className={`${className} ${
          disabled ? 'bg-gray' : 'bg-border'
        } ${betError ? 'border-red' : 'border-indigo'} flex items-center justify-between rounded-lg border border-indigo bg-border px-3 py-2 text-white focus:border-transparent focus:outline-none sm:px-[22px] sm:py-3 md:rounded-xl md:border-2 md:px-6 md:py-[14.5px]`}
      >
        <input
          className={`w-[80%] border-none bg-transparent text-md font-medium text-white outline-none [appearance:textfield] md:text-[18px] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
          type="text"
          value={value}
          onChange={valueHandler}
          disabled={disabled}
          name="bet"
          autoComplete="off"
          {...props}
        />

        <p
          className={`flex text-sm md:text-[14px] ${betError === BetError.LOW_BALANCE ? 'text-[#ED5264]' : 'text-light-grape'}`}
        >
          {t('modals.setBet.yourBalance')}{' '}
          <AnimatedTransition show={Boolean((balance! ?? 0) >= 0)} asFragment>
            <span className="pl-1"> {balance}</span>
          </AnimatedTransition>
        </p>
      </div>

      <span
        data-testid="error-message"
        className="block min-h-4 text-md leading-3 text-[#ED5264] sm:text-[14px] sm:leading-4"
      >
        {errorMessages[betError] ?? <br />}
      </span>
    </div>
  );
};

export default BetInput;
